import { Tag, Timeline, Typography } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Filter from '@/components/Filter';
import Table from '@/components/list/Table';
import StatusBadge from '@/components/order/StatusBadge';
import { PRODUCT_ORDER_STATUS } from '@/constants';
import { useAdminProductOrders } from '@/hooks/queries/admin';
import usePagination from '@/hooks/usePagination';
import type { GetAdminProductOrdersParams } from '@/types/admin';

const ProductOrderList = () => {
  const navigate = useNavigate();
  const pagination = usePagination<GetAdminProductOrdersParams>([
    'branchId',
    'userName',
    'status',
    'isInvoiced',
  ]);
  const query = useAdminProductOrders({
    ...pagination.query,
  });

  return (
    <div css={tw`flex flex-col gap-[16px]`}>
      <Filter
        pagination={pagination}
        filters={[
          {
            name: 'branchId',
            type: 'branch',
            label: '지점명',
            placeholder: '전체',
          },
          {
            name: 'userName',
            type: 'input',
            label: '회원 이름',
            placeholder: 'ex) 김다락',
          },
          {
            name: 'status',
            type: 'select',
            label: '신청 상태',
            placeholder: '전체',
            options: PRODUCT_ORDER_STATUS,
          },
          {
            label: '세금계산서',
            name: 'isInvoiced',
            type: 'select',
            placeholder: '전체',
            options: {
              true: '발행',
              false: '미발행',
            },
            format: (value) => value === 'true',
          },
        ]}
      />

      <Table
        headerConfig={{ showSizeSelector: true, showTotalCount: true }}
        columns={[
          {
            dataIndex: 'id',
            title: 'No',
            width: 70,
          },
          {
            dataIndex: 'userName',
            title: '회원 이름',
            width: 85,
          },
          {
            dataIndex: 'branchName',
            title: '지점명',
            width: 105,
          },
          {
            dataIndex: 'productName',
            title: '상품명',
            width: 'auto',
          },
          {
            dataIndex: 'price',
            title: '정가',
            width: 115,
            align: 'right',
            render: (_, { price }) => `${price.toLocaleString()}원`,
          },
          {
            dataIndex: 'discountPrice',
            title: '할인가',
            width: 115,
            align: 'right',
            render: (_, { discountPrice }) =>
              `${discountPrice.toLocaleString()}원`,
          },
          {
            dataIndex: 'resultPrice',
            title: '판매가',
            width: 115,
            align: 'right',
            render: (_, { price, discountPrice }) =>
              `${(price - discountPrice).toLocaleString()}원`,
          },
          {
            dataIndex: 'cost',
            title: '원가',
            width: 115,
            align: 'right',
            render: (_, { cost }) => `${cost.toLocaleString()}원`,
          },
          {
            dataIndex: 'status',
            title: '신청 상태',
            width: 95,
            render: (_, { status }) => <StatusBadge status={status} />,
          },
          {
            dataIndex: 'date',
            title: '신청일 / 처리일',
            width: 240,
            render: (_, { orderedAt, completedAt }) => (
              <Timeline
                items={[
                  {
                    children: (
                      <>
                        <Typography.Text type="secondary">
                          신청일
                        </Typography.Text>
                        {dayjs(orderedAt).format('YYYY-MM-DD HH:mm')}
                      </>
                    ),
                  },
                  {
                    children: (
                      <>
                        <Typography.Text type="secondary">
                          처리일
                        </Typography.Text>
                        {completedAt
                          ? dayjs(completedAt).format('YYYY-MM-DD HH:mm')
                          : '-'}
                      </>
                    ),
                  },
                ]}
              />
            ),
          },
          {
            dataIndex: 'isInvoiced',
            title: '세금계산서',
            width: 95,
            align: 'center',
            render: (value) => (
              <Tag color="default">{value ? '발행' : '미발행'}</Tag>
            ),
          },
        ]}
        dataSource={query.data?.results}
        emptyText="신청 내역이 없어요"
        onRow={(row) => ({
          onClick: () =>
            navigate(`${PATH.MARKETING_PRODUCT_MANAGE.ORDER}/${row.id}`),
        })}
        rowKey={(row) => row.id}
        pagination={{ ...pagination, total: query.data?.count }}
      />
    </div>
  );
};

export default ProductOrderList;
